import WFSLayer from '@arcgis/core/layers/WFSLayer';
import GeoJSONLayer from '@arcgis/core/layers/GeoJSONLayer';
import MapView from '@arcgis/core/views/MapView';
import { GeoJsonNormalizerUtil } from '@gishub/domain';

/**
 * Resultado de la creación de una capa WFS.
 * Puede ser WFSLayer (nativo) o GeoJSONLayer (fallback con datos reparados).
 */
export interface WfsLayerResult {
  layer: WFSLayer | GeoJSONLayer;
  title: string;
  source: 'wfs-native' | 'wfs-geojson-fallback';
}

/**
 * Factory que encapsula la creación de capas WFS con strategy de fallback.
 *
 * Flujo:
 * 1. Intenta crear WFSLayer (nativo ArcGIS) — funciona con servidores WFS
 *    que emiten GeoJSON válido.
 * 2. Si WFSLayer.load() falla (p. ej. SERNANP: GeoJSON malformado), ejecuta
 *    el fallback: descarga GetFeature → normaliza coordenadas → crea
 *    GeoJSONLayer vía blob URL.
 *
 * Patrón: **Factory Method** con **Strategy** interna.
 */
export class WfsLayerFactory {

  /** Timeout máximo para el intento WFSLayer nativo (ms). */
  private static readonly NATIVE_TIMEOUT_MS = 30_000;

  /**
   * Crea una capa WFS para el servicio y nombre dados.
   * Primero intenta WFSLayer; si falla, intenta GeoJSONLayer con datos reparados.
   *
   * @returns WfsLayerResult o null si ambos intentos fallan.
   */
  static async create(
    serviceUrl: string,
    typeName: string,
    title: string,
    view: MapView,
  ): Promise<WfsLayerResult | null> {
    // 1. Intento nativo: WFSLayer
    const native = await WfsLayerFactory.tryNative(serviceUrl, typeName, title, view);
    if (native) return native;

    // 2. Fallback: GeoJSONLayer con datos reparados
    return WfsLayerFactory.tryGeoJsonFallback(serviceUrl, typeName, title, view);
  }

  /**
   * Intenta crear y cargar un WFSLayer nativo.
   * Timeout configurable para no bloquear la UI.
   */
  private static async tryNative(
    serviceUrl: string,
    typeName: string,
    title: string,
    view: MapView,
  ): Promise<WfsLayerResult | null> {
    if (!view.map) return null;
    const layer = new WFSLayer({ url: serviceUrl, name: typeName, title: `WFS: ${title}` });
    view.map.add(layer);

    try {
      await WfsLayerFactory.withTimeout(
        layer.load(),
        WfsLayerFactory.NATIVE_TIMEOUT_MS,
        'WFSLayer load timeout',
      );
      return { layer, title, source: 'wfs-native' };
    } catch {
      view.map.remove(layer);
      return null;
    }
  }

  /**
   * Fallback: descarga GetFeature GeoJSON, normaliza coordenadas malformadas
   * y crea un GeoJSONLayer con blob URL.
   */
  private static async tryGeoJsonFallback(
    serviceUrl: string,
    typeName: string,
    title: string,
    view: MapView,
  ): Promise<WfsLayerResult | null> {
    try {
      const geojson = await WfsLayerFactory.fetchGetFeatureGeoJson(serviceUrl, typeName);
      if (!geojson || geojson.type !== 'FeatureCollection' || !geojson.features?.length) {
        return null;
      }

      // Normalizar coordenadas malformadas (in-place)
      GeoJsonNormalizerUtil.normalize(geojson);

      // Crear capa vía blob URL (mismo patrón que archivos subidos)
      const blob = new Blob([JSON.stringify(geojson)], { type: 'application/json' });
      const blobUrl = URL.createObjectURL(blob);
      const layer = new GeoJSONLayer({ url: blobUrl, title: `WFS: ${title}` });
      view.map!.add(layer);

      await layer.load();
      return { layer, title, source: 'wfs-geojson-fallback' };
    } catch (err) {
      console.warn(`[WfsLayerFactory] Fallback GeoJSON falló para '${typeName}':`, err);
      return null;
    }
  }

  /**
   * Descarga GetFeature (GEOJSON) de un servicio WFS.
   * Sigue el protocolo WFS 2.0 con OUTPUTFORMAT=GEOJSON.
   */
  private static async fetchGetFeatureGeoJson(serviceUrl: string, typeName: string): Promise<any> {
    const separator = serviceUrl.includes('?') ? '&' : '?';
    const url = `${serviceUrl}${separator}SERVICE=WFS&REQUEST=GetFeature&VERSION=2.0.0`
      + `&TYPENAMES=${encodeURIComponent(typeName)}&OUTPUTFORMAT=GEOJSON&SRSNAME=EPSG:4326`;

    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status} ${response.statusText}`);
    }
    return response.json();
  }

  /** Envuelve una promesa con timeout. Rechaza si excede el tiempo. */
  private static withTimeout<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
    return Promise.race([
      promise,
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error(message)), ms),
      ),
    ]);
  }
}
